<script setup>
import { computed, markRaw } from 'vue';
import CreditCard from '@primeicons/vue/credit-card';
import Sparkles from '@primeicons/vue/sparkles';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';

const creditCardIcon = markRaw(CreditCard);
const sparklesIcon = markRaw(Sparkles);
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();
const item = (name, label, icon, path, params = {}) => ({
  name,
  label,
  icon,
  path,
  to: accountScopedRoute(name, params),
});
const assistantItem = (name, label, icon, activeOn = []) => ({
  name,
  label,
  icon,
  activeOn,
  to: route.params.assistantId
    ? accountScopedRoute(name, { assistantId: route.params.assistantId })
    : accountScopedRoute('captain_assistants_index', { navigationPath: name }),
});
const isAllowed = entry => {
  const { meta } = entry.to.params.navigationPath
    ? router
        .getRoutes()
        .find(record => record.name === entry.to.params.navigationPath)
    : router.resolve(entry.to);
  return shouldShow(meta.featureFlag, meta.permissions, meta.installationTypes);
};
const assistantItems = computed(() => [
  assistantItem(
    'captain_assistants_responses_index',
    t('SIDEBAR.CAPTAIN_RESPONSES'),
    'i-lucide-messages-square',
    ['captain_assistants_responses_pending']
  ),
  assistantItem(
    'captain_assistants_documents_index',
    t('SIDEBAR.CAPTAIN_DOCUMENTS'),
    'i-lucide-files'
  ),
  assistantItem(
    'captain_assistants_scenarios_index',
    t('SIDEBAR.CAPTAIN_SCENARIOS'),
    'i-lucide-workflow'
  ),
  assistantItem(
    'captain_assistants_playground_index',
    t('SIDEBAR.CAPTAIN_PLAYGROUND'),
    'i-lucide-flask-conical'
  ),
  assistantItem(
    'captain_assistants_inboxes_index',
    t('SIDEBAR.CAPTAIN_INBOXES'),
    'i-lucide-inbox'
  ),
  assistantItem(
    'captain_tools_index',
    t('SIDEBAR.CAPTAIN_TOOLS'),
    'i-lucide-wrench'
  ),
  assistantItem(
    'captain_assistants_settings_index',
    t('SIDEBAR.CAPTAIN_SETTINGS'),
    'i-lucide-settings',
    [
      'captain_assistants_guidelines_index',
      'captain_assistants_guardrails_index',
    ]
  ),
]);
const assistantEntryRoute = computed(
  () => assistantItems.value.find(isAllowed)?.to
);
const sections = computed(() => [
  {
    label: t('SIDEBAR.SETTINGS'),
    items: [
      item(
        'profile_settings_index',
        t('SETTINGS_NAV.PROFILE'),
        'i-lucide-user-round',
        '/profile'
      ),
      item(
        'general_settings_index',
        t('SETTINGS_NAV.ACCOUNT'),
        'i-lucide-contact-round',
        '/settings/general'
      ),
      item(
        'billing_settings_index',
        t('SIDEBAR.BILLING'),
        creditCardIcon,
        '/settings/billing'
      ),
    ],
  },
  {
    label: t('SETTINGS_NAV.SERVICE'),
    items: [
      item(
        'settings_inbox_list',
        t('SIDEBAR.YESZAP_CHANNELS'),
        'i-lucide-inbox',
        '/settings/inboxes'
      ),
      item(
        'settings_teams_list',
        t('SETTINGS_NAV.TEAMS'),
        'i-lucide-building-2',
        '/settings/teams'
      ),
      item(
        'labels_list',
        t('SIDEBAR.LABELS'),
        'i-lucide-tags',
        '/settings/labels'
      ),
      item(
        'canned_list',
        t('SIDEBAR.CANNED_RESPONSES'),
        'i-lucide-message-square-text',
        '/settings/canned-response'
      ),
      item(
        'attributes_list',
        t('SIDEBAR.CUSTOM_ATTRIBUTES'),
        'i-lucide-list-filter',
        '/settings/custom-attributes'
      ),
      item(
        'assignment_policy_index',
        t('SIDEBAR.AGENT_ASSIGNMENT'),
        'i-lucide-git-pull-request',
        '/settings/assignment-policy'
      ),
      item(
        'sla_list',
        t('SETTINGS_NAV.SLA'),
        'i-lucide-clock',
        '/settings/sla'
      ),
    ],
  },
  {
    label: t('SETTINGS_NAV.PEOPLE'),
    items: [
      item(
        'agent_list',
        t('SETTINGS_NAV.USERS'),
        'i-lucide-users-round',
        '/settings/agents'
      ),
      item(
        'custom_roles_list',
        t('SIDEBAR.CUSTOM_ROLES'),
        'i-lucide-user-cog',
        '/settings/custom-roles'
      ),
    ],
  },
  {
    label: t('SIDEBAR.YESCRM'),
    items: [
      item(
        'yescrm_settings',
        t('SETTINGS_NAV.PIPELINES'),
        'i-lucide-chart-no-axes-combined',
        '/yescrm/configuracoes'
      ),
    ],
  },
  ...(route.path.includes('/captain')
    ? [
        {
          label: t('SIDEBAR.CAPTAIN'),
          items: assistantItems.value,
        },
      ]
    : []),
  {
    label: t('SETTINGS_NAV.ADMINISTRATION'),
    items: [
      ...(assistantEntryRoute.value
        ? [
            {
              name: 'captain_assistants_index',
              label: t('SIDEBAR.CAPTAIN'),
              icon: sparklesIcon,
              path: '/captain',
              to: assistantEntryRoute.value,
            },
          ]
        : []),
      item(
        'automation_list',
        t('SIDEBAR.AUTOMATION'),
        'i-lucide-workflow',
        '/settings/automation'
      ),
      item(
        'macros_wrapper',
        t('SIDEBAR.MACROS'),
        'i-lucide-zap',
        '/settings/macros'
      ),
      item(
        'agent_bots',
        t('SETTINGS_NAV.BOTS'),
        'i-lucide-bot',
        '/settings/agent-bots'
      ),
      item(
        'settings_applications',
        t('SIDEBAR.INTEGRATIONS'),
        'i-lucide-plug',
        '/settings/integrations'
      ),
      item(
        'security_settings_index',
        t('SIDEBAR.SECURITY'),
        'i-lucide-shield-check',
        '/settings/security'
      ),
      item(
        'auditlogs_list',
        t('SIDEBAR.AUDIT_LOGS'),
        'i-lucide-history',
        '/settings/audit-logs'
      ),
    ],
  },
]);
const visibleSections = computed(() =>
  sections.value
    .map(section => ({
      ...section,
      items: section.items.filter(isAllowed),
    }))
    .filter(section => section.items.length)
);
const isActive = entry =>
  entry.path
    ? route.path.includes(entry.path)
    : route.name === entry.name ||
      route.params.navigationPath === entry.name ||
      entry.activeOn?.includes(route.name);
const activeItem = computed(() =>
  visibleSections.value.flatMap(section => section.items).find(isActive)
);
const navigate = event => {
  const entry = visibleSections.value
    .flatMap(section => section.items)
    .find(option => option.name === event.target.value);
  router.push(entry.to);
};
</script>

<template>
  <div
    class="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:flex-row"
  >
    <aside
      class="hidden w-60 shrink-0 overflow-y-auto border-r border-n-weak bg-n-solid-1 px-3 py-4 lg:block"
    >
      <nav :aria-label="t('SIDEBAR.SETTINGS')" class="grid gap-6">
        <section v-for="section in visibleSections" :key="section.label">
          <h2
            class="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ section.label }}
          </h2>
          <ul class="m-0 grid list-none gap-1 p-0">
            <li v-for="entry in section.items" :key="entry.name">
              <RouterLink
                :to="entry.to"
                :aria-current="isActive(entry) ? 'page' : undefined"
                class="flex min-h-9 items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :class="
                  isActive(entry)
                    ? 'bg-n-brand/10 font-medium text-n-brand'
                    : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
                "
              >
                <Icon :icon="entry.icon" class="size-4 shrink-0" />
                <span>{{ entry.label }}</span>
              </RouterLink>
            </li>
          </ul>
        </section>
      </nav>
    </aside>
    <div
      class="shrink-0 border-b border-n-weak bg-n-solid-1 p-3 pl-14 md:pl-3 lg:hidden"
    >
      <label class="m-0 flex items-center gap-3 text-sm text-n-slate-11">
        <span>{{ t('SIDEBAR.SETTINGS') }}</span>
        <select
          :value="activeItem?.name"
          class="m-0 h-10 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-n-slate-12"
          @change="navigate"
        >
          <optgroup
            v-for="section in visibleSections"
            :key="section.label"
            :label="section.label"
          >
            <option
              v-for="entry in section.items"
              :key="entry.name"
              :value="entry.name"
            >
              {{ entry.label }}
            </option>
          </optgroup>
        </select>
      </label>
    </div>
    <div class="flex min-h-0 min-w-0 flex-1 overflow-hidden">
      <RouterView />
    </div>
  </div>
</template>
